/**
 * Sheet — premium bottom sheet with slide + fade + blur backdrop AND
 * unified drag-to-dismiss gesture.
 *
 * Behaviour (matches the global Link gesture spec):
 *   • Drag downward anywhere on the sheet → the sheet follows the
 *     finger 1:1, the backdrop opacity decreases proportionally.
 *   • Release past the threshold OR with a fast flick → onClose() fires
 *     (parent unmounts via AnimatePresence exit).
 *   • Release short of the threshold → spring back to rest.
 *   • Scroll guard: the consumer can pass a `scrollRef` and drag only
 *     begins once the scroll container is at the top.
 *   • ESC + backdrop tap still dismiss; nothing about the existing API
 *     changes for non-gesture consumers.
 */
import React, { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";

import { SPRING, DURATION, EASE } from "@/shared/motion/tokens";
import { surfaceStyle } from "@/shared/motion/surface";
import { useDragDismiss } from "@/shared/motion/useDragDismiss";

export default function Sheet({
  open,
  onClose,
  children,
  testid,
  className = "",
  backdropClassName = "",
  maxHeight = "92vh",
  rounded = "rounded-t-[32px]",
  ariaLabel,
  // Pass true to disable the swipe-down dismiss gesture (e.g. for
  // sheets that contain a horizontal carousel or a draggable map).
  disableDragDismiss = false,
}) {
  // Body scroll lock while open.
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // ESC to close.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Drag-to-dismiss — bottom sheet ⇒ origin = "bottom" (pull DOWN to
  // close). The inner scroll container ref lets us suppress drag when
  // the user is mid-scroll.
  const scrollRef = useRef(null);
  const { motionProps, backdropOpacity } = useDragDismiss({
    onDismiss: onClose,
    origin: "bottom",
    open,
    scrollRef,
    enabled: !disableDragDismiss,
  });

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="backdrop"
            className={`fixed inset-0 z-[70] bg-black/45 ${backdropClassName}`}
            style={{
              opacity: backdropOpacity,
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: DURATION.normal, ease: EASE.out }}
            onClick={onClose}
            data-testid={testid ? `${testid}-backdrop` : undefined}
          />
          <motion.div
            key="sheet"
            className={`fixed inset-x-0 bottom-0 z-[80] ${rounded} ${className}`}
            style={{ maxHeight, ...surfaceStyle("drawer") }}
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={SPRING.sheet}
            role="dialog"
            aria-modal="true"
            aria-label={ariaLabel}
            data-testid={testid}
            {...(disableDragDismiss ? {} : motionProps)}
          >
            {/* Drag handle — visual affordance + accessible target. */}
            <div className="flex justify-center pt-2.5 pb-1 cursor-grab active:cursor-grabbing select-none" aria-hidden>
              <div className="w-10 h-1 rounded-full bg-black/15" />
            </div>
            {/* Scroll container — passing ref to the drag hook makes
                the dismiss gesture wait until the user is at top. */}
            <div ref={scrollRef} className="overflow-y-auto" style={{ maxHeight }}>
              {children}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
